class PowerGateConfigurator extends HTMLElement {
  async connectedCallback() {
    this.step = 1;
    this.selection = { series: '', voltage: '', horsepower: '', enclosure: '', disconnect: '', bypass: '', reactor: '' };
    this.grid = this.querySelector('[data-pg-data-grid]');
    this.filters = [...this.querySelectorAll('[data-pg-filter]')];
    this.alert = this.querySelector('[data-pg-alert]');
    await this.loadRemainingPages();
    this.readItems();
    this.initDirectSku();
    this.renderSeries();
    this.bind();
    this.refreshFilters();
  }

  async loadRemainingPages() {
    let next = this.querySelector('[data-pg-next-url]')?.dataset.pgNextUrl;
    while (next && this.grid) {
      const url = new URL(next, window.location.origin);
      url.searchParams.set('section_id', this.closest('.shopify-section')?.id?.replace('shopify-section-', '') || '');
      const response = await fetch(url, { credentials: 'same-origin' });
      if (!response.ok) break;
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      doc.querySelectorAll('[data-pg-item]').forEach((node) => this.grid.appendChild(node));
      next = doc.querySelector('[data-pg-next-url]')?.dataset.pgNextUrl;
    }
  }

  readItems() {
    this.items = [...this.querySelectorAll('[data-pg-item]')].map((node) => ({ ...node.dataset }));
  }

  initDirectSku() {
    const select = this.querySelector('[data-pg-sku-select]');
    const result = this.querySelector('[data-pg-sku-result]');
    if (!select || !result) return;

    const sorted = [...this.items].sort((a, b) =>
      (a.sku || '').localeCompare(b.sku || '', undefined, { numeric: true })
    );

    select.replaceChildren(new Option('Select a PowerGate SKU or package', ''));
    sorted.forEach((item, index) => {
      const label = `${item.sku || 'No SKU'} — ${item.title || 'PowerGate package'}`;
      select.add(new Option(label, String(index)));
    });

    select.addEventListener('change', () => {
      if (select.value === '') {
        result.hidden = true;
        return;
      }

      const item = sorted[Number(select.value)];
      if (!item) return;

      this.querySelector('[data-pg-sku-title]').textContent = item.title || 'PowerGate package';
      this.querySelector('[data-pg-sku-code]').textContent = `SKU ${item.sku || 'Not assigned'}`;
      this.querySelector('[data-pg-sku-price]').textContent = item.price || '';
      const link = this.querySelector('[data-pg-sku-link]');
      link.href = item.url || '#';
      result.hidden = false;
    });
  }

  bind() {
    this.querySelector('[data-pg-next]').addEventListener('click', () => this.next());
    this.querySelector('[data-pg-back]').addEventListener('click', () => this.go(this.step - 1));
    this.querySelector('[data-pg-reset]').addEventListener('click', () => this.reset());
    this.querySelectorAll('[data-pg-step-button]').forEach((button) => button.addEventListener('click', () => {
      const target = Number(button.dataset.pgStepButton);
      if (target < this.step) this.go(target);
    }));
    this.filters.forEach((filter) => filter.addEventListener('change', () => {
      this.selection[filter.dataset.pgFilter] = filter.value;
      this.clearLaterFilters(filter.dataset.pgFilter);
      this.refreshFilters();
      this.updateOptionCompatibility();
    }));
    this.querySelectorAll('[data-pg-options] input').forEach((box) => box.addEventListener('change', () => {
      const checked = this.selectedOptions();
      if (checked.length > 7) { box.checked = false; this.showAlert('A maximum of seven installed options can be requested.'); }
    }));
    this.querySelectorAll('[data-pg-submittal]').forEach((field) => field.addEventListener('input', () => this.updateQuoteLink()));
    const submittalDate = this.querySelector('[data-pg-submittal="date"]');
    if (submittalDate && !submittalDate.value) submittalDate.value = new Date().toISOString().slice(0, 10);
    this.querySelector('[data-pg-quote]').addEventListener('click', (event) => {
      if (event.currentTarget.classList.contains('is-disabled')) {
        event.preventDefault();
        this.showAlert('Complete the required project and submittal fields before requesting the quote.');
      }
    });
        const submittalButton = this.querySelector('[data-pg-download-submittal]');
    if (submittalButton) {
      submittalButton.addEventListener('click', () => this.downloadSubmittal());
    }
  }

  renderSeries() {
    const holder = this.querySelector('[data-pg-choices="series"]');
    const series = [...new Set(this.items.map((item) => item.series).filter(Boolean))].sort();
    holder.replaceChildren(...series.map((value) => {
      const button = document.createElement('button');
      button.type = 'button'; button.className = 'pg-series-card'; button.dataset.value = value;
      const isE = / E$/.test(value);
      button.innerHTML = `<strong>${this.escape(value)}</strong><span>${isE ? 'Economy HVAC bypass controller for compact NEMA 1 applications.' : 'Flexible packaged HVAC drive for broad horsepower and enclosure requirements.'}</span><small>${this.items.filter((item) => item.series === value).length} base packages</small>`;
      button.addEventListener('click', () => {
        holder.querySelectorAll('button').forEach((node) => node.classList.remove('is-selected'));
        button.classList.add('is-selected'); this.selection.series = value; this.refreshFilters(); this.updateOptionCompatibility();
      });
      return button;
    }));
  }

  clearLaterFilters(changed) {
    const order = ['voltage', 'horsepower', 'enclosure', 'disconnect', 'bypass', 'reactor'];
    const index = order.indexOf(changed);
    order.slice(index + 1).forEach((key) => { this.selection[key] = ''; });
  }

  refreshFilters() {
    const order = ['voltage', 'horsepower', 'enclosure', 'disconnect', 'bypass', 'reactor'];
    order.forEach((key, index) => {
      const select = this.querySelector(`[data-pg-filter="${key}"]`);
      if (!select) return;
      const previous = this.selection[key];
      const candidates = this.items.filter((item) => this.matches(item, ['series', ...order.slice(0, index)]));
      const values = [...new Set(candidates.map((item) => item[key]).filter(Boolean))];
      values.sort((a, b) => key === 'horsepower' ? parseFloat(a) - parseFloat(b) : a.localeCompare(b, undefined, { numeric: true }));
      select.replaceChildren(new Option(`Select ${key === 'horsepower' ? 'horsepower' : key}`, ''));
      values.forEach((value) => select.add(new Option(key === 'horsepower' ? `${value} HP` : value, value)));
      select.value = values.includes(previous) ? previous : '';
      this.selection[key] = select.value;
      select.disabled = key !== 'voltage' && !this.selection[order[index - 1]];
    });
  }

  matches(item, keys = Object.keys(this.selection)) {
    return keys.every((key) => !this.selection[key] || (item[key] || '').toLowerCase() === this.selection[key].toLowerCase());
  }

  validate() {
    if (this.step === 1 && !this.selection.series) return 'Choose PowerGate E or PowerGate H.';
    if (this.step === 2 && (!this.selection.voltage || !this.selection.horsepower)) return 'Select both voltage and motor horsepower.';
    if (this.step === 3 && ['enclosure', 'disconnect', 'bypass', 'reactor'].some((key) => !this.selection[key])) return 'Complete all package configuration fields.';
    return '';
  }

  next() {
    const error = this.validate();
    if (error) return this.showAlert(error);
    this.go(Math.min(5, this.step + 1));
  }

  go(step) {
    this.step = step; this.alert.hidden = true;
    this.querySelectorAll('[data-pg-step]').forEach((section) => { const active = Number(section.dataset.pgStep) === step; section.hidden = !active; section.classList.toggle('is-active', active); });
    this.querySelectorAll('[data-pg-step-button]').forEach((button) => { const number = Number(button.dataset.pgStepButton); button.classList.toggle('is-active', number === step); button.classList.toggle('is-complete', number < step); });
    this.querySelector('[data-pg-back]').hidden = step === 1;
    this.querySelector('[data-pg-next]').hidden = step === 5;
    this.querySelector('[data-pg-quote]').hidden = step !== 5;
    if (step === 4) this.updateOptionCompatibility();
    if (step === 5) this.renderReview();
    this.querySelector('.pg-wizard').scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  updateOptionCompatibility() {
    const series = / E$/.test(this.selection.series) ? 'E' : 'H';
    const nema1 = this.selection.enclosure === 'NEMA 1';
    const basic = this.selection.bypass === 'Basic Bypass';
    const nonBypass = this.selection.bypass === 'Non-Bypass';
    this.querySelectorAll('[data-series-option]').forEach((label) => {
      const allowed = label.dataset.seriesOption === series && !(label.hasAttribute('data-not-nema1') && nema1) && !(label.hasAttribute('data-not-basic') && basic) && !(label.hasAttribute('data-non-bypass') && !nonBypass);
      label.hidden = !allowed; label.querySelector('input').disabled = !allowed;
      if (!allowed) label.querySelector('input').checked = false;
    });
  }

  selectedOptions() { return [...this.querySelectorAll('[data-pg-options] input:checked')].map((box) => box.value); }

  renderReview() {
    const matches = this.items.filter((item) => this.matches(item));
    const options = this.selectedOptions();
    const network = this.querySelector('[data-pg-network]').value;
    const quantity = this.querySelector('[data-pg-quantity]').value || 1;
    this.querySelector('[data-pg-count]').textContent = matches.length;
    this.querySelector('[data-pg-empty]').hidden = matches.length > 0;
    this.querySelector('[data-pg-summary]').innerHTML = `<h3>Your configuration</h3><dl>${this.summaryRows({ ...this.selection, quantity, network, options: options.length ? options.join(', ') : 'None requested' })}</dl><p>Optional equipment and final engineering compatibility are confirmed during quotation.</p>`;
    const results = this.querySelector('[data-pg-results]');
    results.replaceChildren(...matches.map((item, index) => {
      const label = document.createElement('label'); label.className = 'pg-result';
      const productUrl = item.url || '#';
      label.innerHTML = `<input type="radio" name="pg-base-sku" value="${this.escape(item.sku)}" ${index === 0 ? 'checked' : ''}><a class="pg-result__product-link" href="${this.escape(productUrl)}" aria-label="View ${this.escape(item.sku)} product page"><strong>${this.escape(item.title)}</strong><small>SKU ${this.escape(item.sku)}</small></a><b>${this.escape(item.price)}</b>`;
      label.querySelector('input').dataset.title = item.title; label.querySelector('input').dataset.price = item.price; label.querySelector('input').dataset.url = item.url;
      label.querySelector('input').addEventListener('change', () => this.updateQuoteLink());
      return label;
    }));
    this.updateQuoteLink();
  }

  summaryRows(data) {
    const labels = { series: 'PowerGate type', voltage: 'Voltage', horsepower: 'Horsepower', enclosure: 'Enclosure', disconnect: 'Disconnect', bypass: 'Bypass', reactor: 'Reactor', quantity: 'Quantity', network: 'Network', options: 'Installed options' };
    return Object.entries(data).map(([key, value]) => `<div><dt>${labels[key]}</dt><dd>${this.escape(key === 'horsepower' ? `${value} HP` : String(value))}</dd></div>`).join('');
  }

  updateQuoteLink() {
    const selected = this.querySelector('input[name="pg-base-sku"]:checked');
    const quote = this.querySelector('[data-pg-quote]');
        const submittalButton = this.querySelector('[data-pg-download-submittal]');
    const fields = Object.fromEntries([...this.querySelectorAll('[data-pg-submittal]')].map((field) => [field.dataset.pgSubmittal, field.value.trim()]));
    const required = ['project', 'contractor', 'submitted_by', 'date', 'tag', 'description'];
    if (!selected || required.some((key) => !fields[key])) {
  quote.removeAttribute('href');
  quote.classList.add('is-disabled');
  quote.setAttribute('aria-disabled', 'true');
  if (submittalButton) submittalButton.hidden = true;
  return;
}
    quote.classList.remove('is-disabled');
    quote.removeAttribute('aria-disabled');
        if (submittalButton) submittalButton.hidden = false;
    const options = this.selectedOptions(); const quantity = this.querySelector('[data-pg-quantity]').value || 1;
    const lines = ['VFD Direct PowerGate quote request', '', 'PROJECT / SUBMITTAL INFORMATION', `Project: ${fields.project}`, `Contractor / company: ${fields.contractor}`, `Engineer / consultant: ${fields.engineer || 'Not provided'}`, `Submitted by: ${fields.submitted_by}`, `Submittal date: ${fields.date}`, `Equipment tag: ${fields.tag}`, `Equipment description: ${fields.description}`, '', 'SELECTED POWERGATE PACKAGE', `Base package: ${selected.dataset.title}`, `SKU: ${selected.value}`, `Displayed contractor price: ${selected.dataset.price}`, `Quantity: ${quantity}`, `PowerGate type: ${this.selection.series}`, `Voltage: ${this.selection.voltage}`, `Horsepower: ${this.selection.horsepower} HP`, `Enclosure: ${this.selection.enclosure}`, `Disconnect: ${this.selection.disconnect}`, `Bypass: ${this.selection.bypass}`, `Reactor: ${this.selection.reactor}`, `Network: ${this.querySelector('[data-pg-network]').value}`, `Installed options: ${options.length ? options.join('; ') : 'None requested'}`, '', 'Please confirm compatibility, availability, lead time, final contractor price, and prepare the corresponding engineering submittal.'];
    quote.href = `mailto:${encodeURIComponent(this.dataset.quoteEmail || 'sales@vfddirect.com')}?subject=${encodeURIComponent(`PowerGate quote request - ${selected.value}`)}&body=${encodeURIComponent(lines.join('\n'))}`;
  }
  downloadSubmittal() {
    const selected = this.querySelector('input[name="pg-base-sku"]:checked');
    if (!selected) {
      this.showAlert('Select a base PowerGate package first.');
      return;
    }

    const fields = Object.fromEntries(
      [...this.querySelectorAll('[data-pg-submittal]')].map((field) => [
        field.dataset.pgSubmittal,
        field.value.trim()
      ])
    );

    const safe = (value) => this.escape(String(value || ''));
    const vfdLogoUrl = 'https://cdn.shopify.com/s/files/1/0900/8680/1778/files/VFD_DIRECT_LOGO_1.png?v=1746900614';
const mitsubishiLogoUrl = 'https://cdn.shopify.com/s/files/1/0900/8680/1778/files/IMG_0002.png?v=1786944455';
    const options = this.selectedOptions();
    const quantity = this.querySelector('[data-pg-quantity]')?.value || '1';
    const title = selected.dataset.title || '';
    const sku = selected.value || '';
    const price = selected.dataset.price || '';

    const rows = [
      ['Base package', title],
      ['SKU', sku],
      ['Quantity', quantity],
      ['PowerGate type', this.selection.series],
      ['Voltage', this.selection.voltage],
      ['Horsepower', `${this.selection.horsepower} HP`],
      ['Enclosure', this.selection.enclosure],
      ['Disconnect', this.selection.disconnect],
      ['Bypass', this.selection.bypass],
      ['Reactor', this.selection.reactor],
      ['Network', this.querySelector('[data-pg-network]')?.value || ''],
      ['Installed options', options.length ? options.join(', ') : 'None requested'],
      
    ];

    const documentWindow = window.open('', '_blank');
    if (!documentWindow) {
      this.showAlert('Allow pop-ups to create the preliminary submittal.');
      return;
    }

    documentWindow.document.write(`
<!doctype html>
<html>
<head>
<meta charset="utf-8">
<title>Preliminary Submittal - ${safe(sku)}</title>
<style>
  @page { size: letter; margin: 0.55in; }
  * { box-sizing: border-box; }
  body { font-family: Arial, sans-serif; color: #202020; margin: 0; }
  header { border-bottom: 4px solid #fdb515; padding-bottom: 14px; margin-bottom: 22px; }
  .brand-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
}

.vfd-logo {
  width: 250px;
  max-height: 68px;
  object-fit: contain;
}

.mitsubishi-logo {
  width: 95px;
  max-height: 48px;
  object-fit: contain;
}
  h1 { font-size: 24px; margin: 18px 0 6px; }
  .status { color: #b7192e; font-weight: 700; text-transform: uppercase; }
  h2 { font-size: 17px; background: #f3f5f7; padding: 10px; margin: 22px 0 10px; }
  table { width: 100%; border-collapse: collapse; }
  th, td { border: 1px solid #cfd5da; padding: 8px 10px; text-align: left; vertical-align: top; }
  th { width: 34%; background: #f7f8f9; }
  .notice { margin-top: 22px; padding: 12px; border: 1px solid #b7192e; color: #7c1322; }
  .actions { margin: 20px 0; }
  button { background: #fdb515; border: 0; padding: 12px 20px; font-weight: 700; cursor: pointer; }
  footer { margin-top: 24px; font-size: 11px; color: #666; }
  @media print {
  .actions { display: none; }
  body { zoom: 0.82; }
}
</style>
</head>
<body>
<header>
  <div class="brand-row">
  <img class="vfd-logo" src="${safe(vfdLogoUrl)}" alt="VFD Direct">
  <img class="mitsubishi-logo" src="${safe(mitsubishiLogoUrl)}" alt="Mitsubishi Electric">
</div>
  <h1>PowerGate Engineering Submittal</h1>
  <div class="status">Preliminary — Pending VFD Direct Engineering Approval</div>
</header>

<div class="actions"><button onclick="window.print()">Save / Print PDF</button></div>

<h2>Project and Submittal Information</h2>
<table>
  <tr><th>Project</th><td>${safe(fields.project)}</td></tr>
  <tr><th>Contractor / company</th><td>${safe(fields.contractor)}</td></tr>
  <tr><th>Engineer / consultant</th><td>${safe(fields.engineer || 'Not provided')}</td></tr>
  <tr><th>Submitted by</th><td>${safe(fields.submitted_by)}</td></tr>
  <tr><th>Submittal date</th><td>${safe(fields.date)}</td></tr>
  <tr><th>Equipment tag</th><td>${safe(fields.tag)}</td></tr>
  <tr><th>Equipment description</th><td>${safe(fields.description)}</td></tr>
</table>

<h2>Selected PowerGate Configuration</h2>
<table>
  ${rows.map(([label, value]) => `<tr><th>${safe(label)}</th><td>${safe(value)}</td></tr>`).join('')}
</table>

<div class="notice">
  This preliminary submittal records the customer-selected configuration. Final compatibility,
  ratings, options, pricing and construction remain subject to VFD Direct engineering review and approval.
</div>

<footer>
  Generated by the VFD Direct PowerGate Contractor Portal.
</footer>
</body>
</html>`);

    documentWindow.document.close();
  }
  showAlert(message) { this.alert.textContent = message; this.alert.hidden = false; }
  reset() { window.location.reload(); }
  escape(value) { const span = document.createElement('span'); span.textContent = value || ''; return span.innerHTML; }
}
if (!customElements.get('powergate-configurator')) customElements.define('powergate-configurator', PowerGateConfigurator);